
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config';
import Swal from 'sweetalert2';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import { IconButton } from '@mui/material';
import {
  Box,
  Card,
  Typography,
  Button,
  Divider,
  LinearProgress,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
} from '@mui/material';
import loadingGif from '../../assets/loading.gif';
import './EditOrganizationRoles.css';

const EditOrganizationRole = () => {
  const [roleData, setRoleData] = useState(null);
  const [roleName, setRoleName] = useState('New Role');
  const [permissions, setPermissions] = useState({
    'Dashboard': { read: false },
    'Create Order (Customer)': { create: false },
    'Current Order': { read: false, update: false, delete: false },
    'Finished Order': { read: false, update: false, delete: false },
    'Do Not Proceed': { read: false, update: false, delete: false },
    'Recently Deleted': { read: false, update: false },
    'Tools': { read: false },
    'Organization Settings': { create: false, read: false, update: false, delete: false },
    'Organization Roles': { create: false, read: false, update: false, delete: false },
    'Organization Members': { create: false, read: false, update: false, delete: false },
    'Product Management (Price)': { create: false, read: false, update: false, delete: false },
    'Messages': { read: false },
  });

  const [loading, setLoading] = useState(false);
  const { roleId } = useParams();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAdminStatus = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        navigate('/unauthorized');
        return;
      }

      setLoading(true);

      try {
        const orgEnabledResponse = await axios.get(`${API_BASE_URL}/org/org-enabled`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: localStorage.getItem('selectedOrg'),
          },
        });

        const { isEnabled } = orgEnabledResponse.data;

        if (!isEnabled) {
          Swal.fire({
            title: 'Organization Disabled',
            text: 'This organization has been disabled by the admin. Please contact support for further assistance.',
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate('/unauthorized');
          });
          return;
        }

        const response = await fetch(`${API_BASE_URL}/users/admin/role-info`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: localStorage.getItem('selectedOrg'),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch organization info');
        }

        const data = await response.json();
        setRoleData(data);

        const res = await axios.get(`${API_BASE_URL}/org-roles/role/${roleId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              SelectedOrg: localStorage.getItem('selectedOrg'),
            },
          });
    
          if (res.status !== 200) {
            throw new Error('Failed to fetch role info');
          }
    
        const resData = res.data;
        setRoleName(resData.roleName);
        setPermissions(resData.permissions);


      } catch (error) {
        console.error('Error fetching admin status:', error);
        navigate('/unauthorized');
      } finally {
        setLoading(false);
      }
    };

    fetchAdminStatus();
  }, [navigate]);

  const isCreateButtonEnabled = roleData && roleData.role_count < roleData.max_role;

  const handleUpdateRole = async () => {
    if (!roleName || !permissions) {
      Swal.fire({
        title: 'Error!',
        text: 'Please fill out all fields.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } else {
      // Show a confirmation alert
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'Do you really want to update this role?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, update it!',
        cancelButtonText: 'No, cancel!',
        reverseButtons: true,
      });
  
      // If the user confirmed, proceed with the update
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem('token');
          const orgId = localStorage.getItem('selectedOrg');
    
          const response = await fetch(`${API_BASE_URL}/org-roles/update`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              SelectedOrg: orgId,
            },
            body: JSON.stringify({
              roleId: roleId,
              roleName: roleName,
              permissions: permissions,
            }),
          });
    
          if (response.status === 201) {
            const data = await response.json();
    
            Swal.fire({
              title: 'Success!',
              text: 'Role updated successfully!',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              navigate('/organization/roles');
            });
          } else {
            throw new Error('Failed to update role');
          }
    
        } catch (error) {
          console.error('Error updating role:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to update role.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        }
      }
    }
  };
  
  

  const handleToggle = (feature, action) => {
    setPermissions((prev) => {
      const newPermissions = {
        ...prev,
        [feature]: {
          ...prev[feature],
          [action]: !prev[feature][action],
        },
      };
  
      // Check conditions for permissions
      if (action === 'read') {
        // If 'read' is turned off, turn off 'update' and 'delete'
        if (!newPermissions[feature].read) {
            if (newPermissions[feature].update !== undefined) {
                newPermissions[feature].update = false;
            }
            if (newPermissions[feature].delete !== undefined) {
                newPermissions[feature].delete = false;
            }
        }
      }
  
      if (action === 'update') {
        // If 'update' is turned on, ensure 'read' is also turned on
        if (newPermissions[feature].update) {
            if (newPermissions[feature].read !== undefined) {
                newPermissions[feature].read = true;
            }
        }
        // If 'update' is turned off, turn off 'delete'
        else {
            if (newPermissions[feature].delete !== undefined) {
                newPermissions[feature].delete = false;
            }
        }
      }
  
      if (action === 'delete') {
        // If 'delete' is turned on, ensure both 'read' and 'update' are turned on
        if (newPermissions[feature].delete) {
            if (newPermissions[feature].read !== undefined) {
                newPermissions[feature].read = true;
            }
            if (newPermissions[feature].update !== undefined) {
                newPermissions[feature].update = true;
            }
        }
      }
  
      return newPermissions;
    });
  };  

  if (loading) {
    return (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 64px)',
        backgroundColor: '#fff',
    }}>
        <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
    </div>
    );
  }


  return (
   <Box sx={{ width: '100%', margin: 'auto', mt: 0 }}>
         <Card sx={{ width: '100%', mb: 3 }}>
           <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
          <IconButton onClick={() => navigate('/organization/roles')} sx={{ mr: 2 }}>
            <KeyboardReturnRoundedIcon />
          </IconButton>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            Edit Organization Role
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center" mb={2}>
              <Grid item xs={8}>
                <TextField
                  label="Role Name"
                  value={roleName}
                  onChange={e => setRoleName(e.target.value)}
                  fullWidth
                  multiline
                  rows={1}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateRole}
                  disabled={!isCreateButtonEnabled}
                  sx={{ height: '100%' }}
                >
                  Update Role
                </Button>
              </Grid>
            </Grid>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><b>Features</b></TableCell>
                    <TableCell align="center"><b>Create</b></TableCell>
                    <TableCell align="center"><b>Read</b></TableCell>
                    <TableCell align="center"><b>Update</b></TableCell>
                    <TableCell align="center"><b>Delete</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(permissions).map(([feature, actions]) => (
                    <TableRow key={feature}>
                      <TableCell>{feature}</TableCell>
                      <TableCell align="center">
                        {actions.create !== undefined && (
                          <Switch
                            checked={!!actions.create}
                            onChange={() => handleToggle(feature, 'create')}
                            color="primary"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {actions.read !== undefined && (
                          <Switch
                            checked={!!actions.read}
                            onChange={() => handleToggle(feature, 'read')}
                            color="primary"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {actions.update !== undefined && (
                          <Switch
                            checked={!!actions.update}
                            onChange={() => handleToggle(feature, 'update')}
                            color="primary"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {actions.delete !== undefined && (
                          <Switch
                            checked={!!actions.delete}
                            onChange={() => handleToggle(feature, 'delete')}
                            color="primary"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
        </Box>
      </Card>
    </Box>
  );
};

export default EditOrganizationRole;
